import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, SectionCard, StatRow, StatTile } from '@/components/ui/section-card';
import { Charge } from '@/types/charge';
import { ChargeException } from '@/types/charge-exception';
import { Enrollments } from '@/types/enrollments';
import { EXCEPTION_ACTION } from '@/types/exception-action';
import { REMINDER_TYPE } from '@/types/reminder-type';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate as formatDateOnly } from '@/utils/format-date';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Pressable, View } from 'react-native';
import { ChargeList } from './charge-list';

type EnrollmentChargeTabProps = {
  enrollment: Enrollments | null;
  onChargePress: (charge: Charge) => void;
  onCreateManualCharge?: () => void;
  onAddException?: (defaultDate?: Date) => void;
  onEditException?: (exception: ChargeException) => void;
  onDeleteException?: (exception: ChargeException) => void;
};

/**
 * Aba de leitura da cobrança de um contrato. A cobrança hoje é sempre mensal
 * (assinatura): o único parâmetro é o dia do mês e o lembrete. Modelo de
 * cobrança / intervalo / cobrança única saíram do produto.
 */
export function EnrollmentChargeTab({
  enrollment,
  onChargePress,
  onCreateManualCharge,
  onAddException,
  onEditException,
  onDeleteException,
}: EnrollmentChargeTabProps) {
  if (!enrollment) return null;

  const chargeData = enrollment.chargeSchedule;
  const exceptions = enrollment.chargeExceptions || [];
  const anyEnrollment = enrollment as Enrollments & {
    bankName?: string;
    bankAgency?: string;
    bankAccount?: string;
    paymentInstructions?: string;
  };

  const chargeDay = chargeData?.chargeDay;

  const getReminderLabel = () => {
    const type = chargeData?.reminderType;
    if (type === REMINDER_TYPE.CUSTOM_DAYS_BEFORE_AND_ON_DUE) {
      return chargeData?.customReminderDays
        ? `${chargeData.customReminderDays} dias antes + no dia`
        : 'Dias antes + no dia';
    }
    if (type === REMINDER_TYPE.ONLY_ON_DUE_DATE || type === REMINDER_TYPE.ON_DUE_DATE) {
      return 'Só no dia do vencimento';
    }
    if (type === REMINDER_TYPE.FIVE_DAYS_BEFORE_AND_ON_DUE) {
      return '5 dias antes + no dia';
    }
    return '3 dias antes + no dia';
  };

  const getExceptionActionLabel = (action: EXCEPTION_ACTION) => {
    switch (action) {
      case EXCEPTION_ACTION.SKIP:
        return 'Pular cobrança';
      case EXCEPTION_ACTION.POSTPONE:
        return 'Adiamento';
      case EXCEPTION_ACTION.MODIFY_AMOUNT:
        return 'Alteração de valor';
      default:
        return action;
    }
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return '-';
    // Campos "data pura" (YYYY-MM-DD) precisam virar meia-noite local, senão
    // new Date() os lê como meia-noite UTC e exibe o dia anterior em UTC-3.
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
      return formatDateOnly(date);
    }
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  };

  const hasPaymentData =
    !!enrollment.pixCopyPaste ||
    !!enrollment.paymentLink ||
    !!anyEnrollment.bankName ||
    !!anyEnrollment.bankAccount ||
    !!anyEnrollment.paymentInstructions;

  return (
    <View className="w-full gap-5 px-5 pt-5">
      <SectionCard icon="repeat-outline" title="Cobrança">
        <StatRow>
          <StatTile label="Valor" value={formatCurrency(enrollment.price)} emphasis />
          <StatTile
            label="Dia do mês"
            value={chargeDay != null ? `Dia ${chargeDay}` : 'Não definido'}
          />
        </StatRow>
        <Field
          label="Frequência"
          value="Todo mês, no dia da cobrança"
          hint="A cobrança da assinatura sempre roda mensalmente."
        />
        <Field label="Lembretes" value={getReminderLabel()} />
      </SectionCard>

      <SectionCard icon="card-outline" title="Dados para pagamento">
        {enrollment.pixCopyPaste ? (
          <View className="gap-2">
            <ThemedText className="text-xs text-neutral-500">Pix copia e cola</ThemedText>
            <View className="rounded-2xl bg-neutral-50 p-3" style={{ borderCurve: 'continuous' }}>
              <ThemedText selectable className="font-mono text-xs text-neutral-900" numberOfLines={4}>
                {enrollment.pixCopyPaste}
              </ThemedText>
            </View>
          </View>
        ) : null}

        {enrollment.paymentLink ? (
          <Field label="Link de pagamento" value={enrollment.paymentLink} />
        ) : null}

        {anyEnrollment.bankName || anyEnrollment.bankAccount ? (
          <View className="gap-4">
            <Field label="Banco" value={anyEnrollment.bankName} />
            <StatRow>
              <StatTile label="Agência" value={anyEnrollment.bankAgency || '—'} />
              <StatTile label="Conta" value={anyEnrollment.bankAccount || '—'} />
            </StatRow>
          </View>
        ) : null}

        {anyEnrollment.paymentInstructions ? (
          <Field label="Instruções de pagamento" value={anyEnrollment.paymentInstructions} />
        ) : null}

        {!hasPaymentData ? (
          <ThemedText className="text-sm text-neutral-400">
            Nenhum dado de pagamento neste contrato. Você pode informar Pix, link ou dados bancários
            ao editar o contrato ou ao criar cada cobrança.
          </ThemedText>
        ) : null}
      </SectionCard>

      <Button onPress={onCreateManualCharge} className="flex-row items-center gap-2">
        <Ionicons name="add-circle-outline" size={18} color="#fff" />
        <ThemedText className="font-semibold text-white">Criar cobrança manual</ThemedText>
      </Button>

      <Card className="p-5">
        <View className="flex-row items-center gap-2.5">
          <View className="h-8 w-8 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
            <Ionicons className="text-primary dark:text-white" name="alert-circle-outline" size={16} />
          </View>
          <ThemedText className="flex-1 text-sm font-bold text-neutral-900">
            Exceções ({exceptions.length})
          </ThemedText>
          <Button size="sm" variant="outline" onPress={() => onAddException?.()}>
            <ThemedText className="text-xs font-semibold">Adicionar</ThemedText>
          </Button>
        </View>

        <View className="mt-4 gap-3">
          {exceptions.length === 0 ? (
            <ThemedText className="text-sm text-neutral-400">
              Nenhuma exceção. Use para pular, adiar ou mudar o valor de uma cobrança específica.
            </ThemedText>
          ) : (
            exceptions.map((exception) => (
              <View
                key={exception.id}
                className="gap-3 rounded-2xl bg-neutral-50 p-3"
                style={{ borderCurve: 'continuous' }}
              >
                <View className="flex-row items-start justify-between gap-2">
                  <View className="min-w-0 flex-1">
                    <ThemedText className="text-base font-semibold text-neutral-900">
                      {getExceptionActionLabel(exception.action)}
                    </ThemedText>
                    <ThemedText className="text-xs text-neutral-500">
                      Cobrança de {formatDate(exception.originalChargeDate)}
                    </ThemedText>
                  </View>
                  <View className="flex-row gap-1">
                    <Pressable onPress={() => onEditException?.(exception)} hitSlop={8} className="p-2">
                      <Ionicons className="text-neutral-600" name="create-outline" size={18} />
                    </Pressable>
                    <Pressable
                      onPress={() => onDeleteException?.(exception)}
                      hitSlop={8}
                      className="p-2"
                    >
                      <Ionicons className="text-red-700" name="trash-outline" size={18} />
                    </Pressable>
                  </View>
                </View>

                {exception.newDueDate || exception.newAmount ? (
                  <StatRow>
                    {exception.newDueDate ? (
                      <StatTile label="Novo vencimento" value={formatDate(exception.newDueDate)} />
                    ) : null}
                    {exception.newAmount ? (
                      <StatTile
                        label="Novo valor"
                        value={exception.newAmount.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                        emphasis
                      />
                    ) : null}
                  </StatRow>
                ) : null}

                {exception.reason ? <Field label="Motivo" value={exception.reason} /> : null}
              </View>
            ))
          )}
        </View>
      </Card>

      <ChargeList enrollmentId={enrollment.id} onChargePress={onChargePress} />
    </View>
  );
}
